import { useState } from 'react';
import { IoAdd } from 'react-icons/io5';
import { Loader } from '@mantine/core';
import { useFilterSets } from '@modules/filter-sets';
import { useCustomSets } from '@modules/custom-sets';
import { HomeTileType } from '@modules/home-tiles';
import { Breadcrumbs } from '@components';
import { Button, Modal, RefetchButton } from '@shared/ui';
import SetCard from './components/SetCard';
import FilterSetForm from '@modules/filter-sets/components/FilterSetForm';
import CustomSetForm from '@modules/custom-sets/components/CustomSetForm';
import '@shared/styles/page.css';
import styles from './CollectionsPage.module.css';

export default function CollectionsPage() {
    const [isFilterSetModalOpen, setIsFilterSetModalOpen] = useState(false);
    const [isCustomSetModalOpen, setIsCustomSetModalOpen] = useState(false);
    const [editingFilterSet, setEditingFilterSet] = useState<number | null>(null);
    const [editingCustomSet, setEditingCustomSet] = useState<number | null>(null);

    const { data: filterSets = [], isLoading: filterSetsLoading, error: filterSetsError, refetch: refetchFilterSets } = useFilterSets();
    const { data: customSets = [], isLoading: customSetsLoading, error: customSetsError, refetch: refetchCustomSets } = useCustomSets();

    const getFilterSetsContent = () => {
        if (filterSetsLoading) {
            return (
                <div className={styles.loaderContainer}>
                    <Loader />
                </div>
            );
        }

        if (filterSetsError) {
            return (
                <RefetchButton
                    refetch={() => refetchFilterSets()}
                    isLoading={filterSetsLoading}
                    errorMessage="Не удалось загрузить подборки по фильтрам"
                />
            );
        }

        return (
            <div className={styles.cardsGrid}>
                {filterSets.length === 0 ? (
                    <div className={styles.emptyMessage}>Нет подборок по фильтрам</div>
                ) : (
                    filterSets.map((filterSet) => (
                        <SetCard
                            key={filterSet.id}
                            title={filterSet.title}
                            description={filterSet.description}
                            imageUrl={filterSet.imageUrl}
                            path={`/sets/filter/${filterSet.id}`}
                            tileType={HomeTileType.FILTERS}
                            setId={filterSet.id}
                            setType="filter"
                            onEdit={() => setEditingFilterSet(filterSet.id)}
                        />
                    ))
                )}
            </div>
        );
    };

    const getCustomSetsContent = () => {
        if (customSetsLoading) {
            return (
                <div className={styles.loaderContainer}>
                    <Loader />
                </div>
            );
        }

        if (customSetsError) {
            return (
                <RefetchButton
                    refetch={() => refetchCustomSets()}
                    isLoading={customSetsLoading}
                    errorMessage="Не удалось загрузить кастомные подборки"
                />
            );
        }

        return (
            <div className={styles.cardsGrid}>
                {customSets.length === 0 ? (
                    <div className={styles.emptyMessage}>Нет кастомных подборок</div>
                ) : (
                    customSets.map((customSet) => (
                        <SetCard
                            key={customSet.id}
                            title={customSet.name}
                            description={customSet.description}
                            imageUrl={customSet.imageUrl}
                            path={`/sets/${customSet.id}`}
                            tileType={HomeTileType.CUSTOM_COLLECTION}
                            setId={customSet.id}
                            setType="custom"
                            onEdit={() => setEditingCustomSet(customSet.id)}
                        />
                    ))
                )}
            </div>
        );
    };

    return (
        <div className="page-container">
            <Breadcrumbs items={[
                { label: 'Главная', path: '/' },
                { label: 'Подборки' }
            ]} />

            <div className="page-header">
                <h1>Подборки товаров</h1>
            </div>

            <div className="content-wrapper">
                {/* Подборки по фильтрам */}
                <div className={styles.collectionsSection}>
                    <div className={styles.sectionHeader}>
                        <h3>Подборки по фильтрам</h3>
                        <Button variant="icon" onClick={() => setIsFilterSetModalOpen(true)}>
                            <IoAdd />
                        </Button>
                    </div>
                    {getFilterSetsContent()}
                </div>

                {/* Кастомные подборки */}
                <div className={styles.collectionsSection}>
                    <div className={styles.sectionHeader}>
                        <h3>Кастомные подборки</h3>
                        <Button variant="icon" onClick={() => setIsCustomSetModalOpen(true)}>
                            <IoAdd />
                        </Button>
                    </div>
                    {getCustomSetsContent()}
                </div>
            </div>

            {/* Модалка для создания/редактирования подборки по фильтрам */}
            <Modal
                isOpen={isFilterSetModalOpen || editingFilterSet !== null}
                onClose={() => {
                    setIsFilterSetModalOpen(false);
                    setEditingFilterSet(null);
                }}
                title={editingFilterSet ? "Редактировать подборку по фильтрам" : "Создать подборку по фильтрам"}
            >
                <FilterSetForm
                    filterSet={editingFilterSet ? filterSets.find(fs => fs.id === editingFilterSet) : undefined}
                    onSuccess={() => {
                        setIsFilterSetModalOpen(false);
                        setEditingFilterSet(null);
                    }}
                />
            </Modal>

            {/* Модалка для создания/редактирования кастомной подборки */}
            <Modal
                isOpen={isCustomSetModalOpen || editingCustomSet !== null}
                onClose={() => {
                    setIsCustomSetModalOpen(false);
                    setEditingCustomSet(null);
                }}
                title={editingCustomSet ? "Редактировать кастомную подборку" : "Создать кастомную подборку"}
            >
                <CustomSetForm
                    customSet={editingCustomSet ? customSets.find(cs => cs.id === editingCustomSet) : undefined}
                    onSuccess={() => {
                        setIsCustomSetModalOpen(false);
                        setEditingCustomSet(null);
                    }}
                />
            </Modal>
        </div>
    );
}
